// Ceilings on typed size fields, declared per product in ProductProfile.maxInches.
//
// Flame Cells is the only user today: Cell OD may not exceed 99.99" (Kevin, 2026-10-02),
// because operation 13 writes the OD into the part number in thousandths and 100" would
// turn that 5-digit segment into a 6-digit one. The field flags an over-limit value as it
// is typed (Question.tsx) and Save and Submit refuses it (SaveSubmitButton), the same two
// halves the Housing Height check has.

import type { AnswerState } from "../types";
import { getProfile } from "../products";

/** Inches from a typed size, read EXACTLY as operation 13's parseInputValue reads it for
 *  the part number: quotes stripped, "7-5/16" / "7 5/16" / "5/16" as fractions, anything
 *  else through parseFloat. Mirroring that reader is the point -- a stricter one would let
 *  through a value the part number still sees as over the cap. Null when unreadable. */
export function inchesOf(raw: unknown): number | null {
  if (raw == null) return null;
  const s = String(raw).replace(/["']/g, "").trim();
  if (!s) return null;
  if (s.includes("/") || s.includes("-")) {
    const m = s.match(/^(\d+)?\s*[- ]?\s*(\d+)\/(\d+)$/);
    if (!m) return null;
    const den = parseInt(m[3], 10);
    return den ? (parseInt(m[1], 10) || 0) + parseInt(m[2], 10) / den : null;
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

/** The ceiling this value breaks, or null when it is within it (or blank / unreadable --
 *  a missing OD is a different problem, reported by the required-field check). Compared in
 *  whole thousandths, the unit the part number uses, so float noise cannot tip 99.99 over. */
export function sizeOverLimit(product: string, key: string, raw: unknown): number | null {
  const max = getProfile(product).maxInches.get(key);
  if (max == null) return null;
  const v = inchesOf(raw);
  if (v == null) return null;
  return Math.round(v * 1000) > Math.round(max * 1000) ? max : null;
}

/** Every capped field currently over its ceiling. */
export function findOverLimitSizes(
  product: string,
  answers: AnswerState
): Array<{ key: string; code: string; value: string; max: number }> {
  const out: Array<{ key: string; code: string; value: string; max: number }> = [];
  for (const key of getProfile(product).maxInches.keys()) {
    const max = sizeOverLimit(product, key, answers[key]);
    if (max != null) out.push({ key, code: key.split("/")[0], value: String(answers[key]), max });
  }
  return out;
}
